/**
 * NotificationItem - Single notification display
 * 
 * Shows:
 * - Actor avatar
 * - Message with actor name highlighted
 * - Time since notification
 * - Unread indicator
 */

import React from 'react';
import { Link } from 'react-router-dom';
import HighlightedAvatar from './HighlightedAvatar';
import { formatTimeAgo } from '../utils/timeUtils';
import { 
  FiMessageCircle, 
  FiHeart, 
  FiCornerUpLeft, 
  FiAtSign, 
  FiUserPlus,
  FiUserMinus,
  FiFileText,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiStar,
  FiBell,
  FiMap,
} from 'react-icons/fi';

// Verb to icon component mapping with colors (Facebook-style)
const getVerbIcon = (verb) => {
  const icons = {
    comment: { Icon: FiMessageCircle, color: '#4267B2', bgColor: '#E7F3FF' }, // Facebook blue
    like: { Icon: FiHeart, color: '#E1306C', bgColor: '#FFE6F0' }, // Instagram pink/red
    reply: { Icon: FiCornerUpLeft, color: '#1DA1F2', bgColor: '#E1F5FE' }, // Twitter blue
    mention: { Icon: FiAtSign, color: '#6C5CE7', bgColor: '#EDECFE' }, // Purple
    follow: { Icon: FiUserPlus, color: '#00C896', bgColor: '#E0F7F1' }, // Green
    post: { Icon: FiFileText, color: '#FD7E14', bgColor: '#FFF3E8' }, // Orange
    post_approved: { Icon: FiCheckCircle, color: '#00C896', bgColor: '#E0F7F1' }, // Green
    post_rejected: { Icon: FiXCircle, color: '#FF6B6B', bgColor: '#FFE5E5' }, // Red
    user_highlighted: { Icon: FiStar, color: '#FFC107', bgColor: '#FFF8E1' }, // Amber
    system: { Icon: FiBell, color: '#6C757D', bgColor: '#F5F5F5' }, // Gray
    // Trip / Companions
    trip_request:          { Icon: FiMap,         color: '#4A90E2', bgColor: '#EBF4FF' }, // Blue
    trip_request_approved: { Icon: FiCheckCircle, color: '#22c55e', bgColor: '#DCFCE7' }, // Green
    trip_request_rejected: { Icon: FiXCircle,     color: '#ef4444', bgColor: '#FEE2E2' }, // Red
    trip_request_canceled: { Icon: FiUserMinus,   color: '#6B7280', bgColor: '#F3F4F6' }, // Gray
    trip_request_removed:  { Icon: FiUserMinus,   color: '#F97316', bgColor: '#FFF7ED' }, // Orange
    trip_deleted:          { Icon: FiAlertCircle, color: '#ef4444', bgColor: '#FEE2E2' }, // Red
  };

  return icons[verb] || icons.system;
};

// DefaultAvatar is now handled by HighlightedAvatar internally

const NotificationItem = ({ notification, onClick }) => {
  const {
    id,
    verb,
    actor_id,
    actor_username,
    actor_image,
    actor_is_highlighted,
    actor_is_private,
    message,
    is_read,
    created_at,
    timesince,
    extra_data,
  } = notification;

  let timeAgo = timesince || formatTimeAgo(created_at);
  
  // Add "назад" if not already present and not "только что"
  if (timeAgo && timeAgo !== 'только что' && !timeAgo.includes('назад')) {
    timeAgo = `${timeAgo} назад`;
  }
  
  // For USER_HIGHLIGHTED notifications, don't show icon overlay
  const showIcon = verb !== 'user_highlighted';
  
  // Get icon configuration
  const iconConfig = getVerbIcon(verb);
  const { Icon, color, bgColor } = iconConfig;
  
  // Avatar will be handled by HighlightedAvatar with DefaultAvatar fallback
  const avatarSrc = actor_image;
  const avatarAlt = actor_username || 'System';

  // Handle avatar click - prevent notification onClick and navigate to profile
  const handleAvatarClick = (e) => {
    e.stopPropagation(); // Prevent notification onClick from firing
  };

  // Render avatar area - make it a link if actor_id exists
  const renderAvatar = () => {
    if (verb === 'user_highlighted') {
      // For highlighted user notification, show a special icon/avatar
      return (
        <div className="notification-item__avatar-special">
          <FiStar size={24} style={{ color: '#FFC107' }} />
        </div>
      );
    }

    const avatarContent = (
      <>
        <HighlightedAvatar
          src={avatarSrc}
          alt={avatarAlt}
          username={actor_username}
          userId={actor_id}
          isHighlighted={actor_is_highlighted || false}
          isPrivate={actor_is_private || false}
          className="notification-item__avatar-img"
          imageProps={{
            priority: false,
            lazy: true
          }}
        />
        {showIcon && (
          <span 
            className="notification-item__icon"
            style={{ 
              '--icon-color': color,
              '--icon-bg-color': bgColor,
            }}
          >
            <Icon size={14} style={{ color: color }} />
          </span>
        )}
      </>
    );

    // Wrap in Link if actor_id exists, otherwise just a div
    if (actor_id) {
      return (
        <Link 
          to={`/author/${actor_id}`}
          onClick={handleAvatarClick}
          className="notification-item__avatar-link"
        >
          {avatarContent}
        </Link>
      );
    }

    return avatarContent;
  };

  return (
    <div
      className={`notification-item ${!is_read ? 'notification-item--unread' : ''}`}
      onClick={onClick}
      data-notification-id={id}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && onClick()}
    >
      {/* Avatar */}
      <div className="notification-item__avatar">
        {renderAvatar()}
      </div>

      {/* Content */}
      <div className="notification-item__content">
        <p className="notification-item__message">
          {message || 'Новое уведомление'}
        </p>
        
        {/* Preview of comment/reply if available */}
        {(extra_data?.comment_preview || extra_data?.reply_preview) && (
          <p className="notification-item__preview">
            "{extra_data.comment_preview || extra_data.reply_preview}"
          </p>
        )}
        
        <span className="notification-item__time">{timeAgo}</span>
      </div>

      {/* Unread indicator */}
      {!is_read && (
        <div className="notification-item__unread-dot"></div>
      )}
    </div>
  );
};

export default NotificationItem;

